import React from 'react'
import styled from 'styled-components'
import { Typography } from '../Typography'
import { Icon, type IconProps, type IconType } from '../Icon'

const SegmentedButtonContainer = styled.div`
  position: relative;
  border-radius: 8px;
  background: var(--surface-tertiary, #e2e6ed);
  display: flex;
  height: 40px;
  padding: 4px;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  overflow: hidden;
`

const Slider = styled.div<{ left: number; width: number }>`
  position: absolute;
  top: 4px;
  bottom: 4px;
  left: ${({ left }) => left}px;
  width: ${({ width }) => width}px;
  background: var(--surface-primary, #fff);
  border-radius: var(--radius-sm, 8px);
  transition: left 0.3s ease, width 0.3s ease, opacity 0.3s ease;
  z-index: 1;
  opacity: 1;
`

const SegmentedButtonOption = styled.button<{ active?: boolean }>`
  flex: 1;
  display: flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  border-radius: var(--radius-sm, 8px);
  color: ${({ active }) => (active ? 'var(--text-primary)' : 'var(--text-secondary, #626875)')};
  transition: color 0.3s ease;
  position: relative;
  z-index: 2;
  white-space: nowrap;

  &:hover {
    color: var(--text-primary);
  }

  &:focus {
    outline: none;
  }
`

export type SegmentedOption<T extends string = string> = {
  icon?: IconType
  label: React.ReactNode
  value: T
  iconProps?: IconProps
  buttonProps?: React.HTMLAttributes<HTMLButtonElement>
  labelProps?: React.HTMLAttributes<HTMLDivElement>
}

export type SegmentedButtonProps<T extends string = string> = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onClick' | 'value'
> & {
  value?: T
  options: SegmentedOption<T>[]
  selectedProps?: React.ComponentProps<typeof SegmentedButtonOption>
  unselectedProps?: React.ComponentProps<typeof SegmentedButtonOption>
  onClick?: (value: T, index: number) => void
}

const SegmentedButtonComponent = <T extends string = string>(
  {
    options = [],
    onClick,
    selectedProps,
    unselectedProps,
    value,
    ...props
  }: SegmentedButtonProps<T>,
  ref: React.Ref<HTMLDivElement>
) => {
  const [slider, setSlider] = React.useState({ left: 0, width: 0 })
  const optionRefs = React.useRef<(HTMLButtonElement | null)[]>([])

  // Find the active index based on the current `value` prop
  const activeIndex = React.useMemo(
    () => getDefaultIndex(options, value),
    [options, value]
  )

  const handleOptionClick = React.useCallback(
    (index: number, option: SegmentedOption<T>) => {
      onClick?.(option.value, index)
    },
    [onClick]
  )

  const updateSlider = React.useCallback(() => {
    const activeOption = optionRefs.current[activeIndex]
    if (activeOption) {
      const { offsetLeft, offsetWidth } = activeOption
      setSlider({ left: offsetLeft, width: offsetWidth })
    }
  }, [activeIndex])

  React.useLayoutEffect(() => {
    updateSlider()
  }, [activeIndex, updateSlider, options])

  React.useLayoutEffect(() => {
    const activeElement = optionRefs.current[activeIndex]
    if (!activeElement) return

    const resizeObserver = new ResizeObserver(() => {
      updateSlider()
    })

    resizeObserver.observe(activeElement)

    return () => {
      resizeObserver.disconnect()
    }
  }, [activeIndex, updateSlider])

  return (
    <SegmentedButtonContainer ref={ref} {...props}>
      <Slider left={slider.left} width={slider.width} />
      {options.map((option, index) => {
        const isActive = activeIndex === index
        const applyProps = isActive ? selectedProps : unselectedProps
        const applyColor = applyProps?.color

        return (
          <SegmentedButtonOption
            key={`option-${option.value}`}
            active={isActive}
            onClick={(e: any) => {
              e.preventDefault()
              handleOptionClick(index, option)
            }}
            {...applyProps}
            ref={(el: HTMLButtonElement | null) => {
              optionRefs.current[index] = el
            }}
            {...option.buttonProps}
          >
            {option.icon && (
              <Icon
                type={option.icon}
                width={24}
                height={24}
                color={applyColor}
                {...option.iconProps}
              />
            )}
            <Typography
              variant='link-sm'
              css={{ color: applyColor ?? 'inherit' }}
              {...option.labelProps}
            >
              {option.label}
            </Typography>
          </SegmentedButtonOption>
        )
      })}
    </SegmentedButtonContainer>
  )
}

const getDefaultIndex = <T extends string>(
  options: SegmentedOption<T>[],
  value?: T
): number => {
  if (value === undefined) return 0
  const index = options.findIndex((option) => option.value === value)
  return index === -1 ? 0 : index
}

export default React.forwardRef(SegmentedButtonComponent) as <
  T extends string = string,
>(
  props: SegmentedButtonProps<T> & { ref?: React.Ref<HTMLDivElement> }
) => JSX.Element
